import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { storeReturnsApi } from '../services/api';
import { MONEY_PATTERN, describeDecideError, formatMoney, isFullAmount } from './storeReturnsUtils';

/**
 * Decide one escalated return. Two steps: fill in the decision, then an explicit confirmation
 * (the decision is final and approving moves money). On an unknown-outcome error the dialog stays
 * on the confirmation step so the admin can repeat the identical call, which the server treats
 * idempotently.
 */
const ReturnDecisionDialog = ({ item, onClose, onDecided }) => {
  const { returnRequest: ret, order } = item;
  const refundable = Number(order.refundableAmount);
  const currency = order.currencyCode;

  const [decision, setDecision] = useState('approve');
  const [amount, setAmount] = useState(String(refundable));
  const [restock, setRestock] = useState(false);
  const [note, setNote] = useState('');
  const [step, setStep] = useState('form');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const approving = decision === 'approve';
  const parsedAmount = Number(amount);
  const amountValid = !approving
    || (MONEY_PATTERN.test(amount.trim()) && parsedAmount > 0 && parsedAmount <= refundable + 0.005);
  const partial = approving && amountValid && !isFullAmount(parsedAmount, refundable);
  const noteRequired = !approving || partial;
  const noteValid = !noteRequired || note.trim().length > 0;
  const canReview = amountValid && noteValid;

  const payload = () => {
    const body = { decision };
    if (approving) {
      // Omitted amount = the whole remaining balance (goods + delivery).
      if (partial) body.amount = parsedAmount;
      body.restock = restock;
    }
    if (note.trim()) body.note = note.trim();
    return body;
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await storeReturnsApi.decide(ret.id, payload());
      onDecided(result, decision);
    } catch (e) {
      setError(describeDecideError(e, decision));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    // A stale error means the list is out of date; refresh it on the way out.
    if (error?.stale || error?.refresh) onDecided(null, null);
    else onClose();
  };

  const refundLabel = formatMoney(partial ? parsedAmount : refundable, currency);

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Decide return #{ret.id} (order #{order.orderId})</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {order.storeName || 'Store'} / {order.buyerName || 'Buyer'} — refundable balance{' '}
          {formatMoney(refundable, currency)}. An admin decision is final: the buyer cannot dispute it.
        </Typography>

        {error && <Alert severity={error.retrySafe ? 'warning' : 'error'} sx={{ mb: 2 }}>{error.message}</Alert>}

        {step === 'form' && (
          <>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel id="decision-label">Decision</FormLabel>
              <RadioGroup
                row
                aria-labelledby="decision-label"
                value={decision}
                onChange={(e) => { setDecision(e.target.value); setError(null); }}
              >
                <FormControlLabel value="approve" control={<Radio />} label="Approve and refund" />
                <FormControlLabel value="reject" control={<Radio />} label="Reject" />
              </RadioGroup>
            </FormControl>

            {approving && (
              <>
                <TextField
                  fullWidth
                  sx={{ mb: 2 }}
                  label={`Refund amount (${currency || ''})`.trim()}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  error={!amountValid}
                  helperText={
                    amountValid
                      ? 'The full balance returns the whole commission; a smaller amount returns it pro rata.'
                      : `Enter an amount above 0 and up to ${formatMoney(refundable, currency)}, with at most 2 decimals.`
                  }
                  inputProps={{ inputMode: 'decimal' }}
                />
                <FormControlLabel
                  control={<Checkbox checked={restock} onChange={(e) => setRestock(e.target.checked)} />}
                  label="Return the items to stock"
                />
              </>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 1 }}
              label={noteRequired ? 'Note (required)' : 'Note (optional)'}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 1000 }}
              error={noteRequired && note.length > 0 && !noteValid}
              helperText={
                !approving ? 'Shown to the buyer and the store as the reason for rejecting.'
                  : partial ? 'Explain why less than the full amount is refunded.' : ' '
              }
            />
          </>
        )}

        {step === 'confirm' && (
          <Box>
            <Alert severity={approving ? 'warning' : 'info'} sx={{ mb: 2 }}>
              {approving
                ? `You are about to refund ${refundLabel} to the buyer. This cannot be undone.`
                : 'You are about to reject this return. The buyer will not be able to dispute it again.'}
            </Alert>
            {note.trim() && (
              <Typography variant="body2">Note: {note.trim()}</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        {step === 'form' ? (
          <Button variant="contained" onClick={() => setStep('confirm')} disabled={!canReview}>
            Review decision
          </Button>
        ) : (
          <>
            {/* After an unknown outcome a repeat settles the ORIGINAL decision, so editing is closed off. */}
            <Button onClick={() => { setStep('form'); setError(null); }} disabled={saving || !!error?.locked}>Back</Button>
            <Button
              variant="contained"
              color={approving ? 'error' : 'primary'}
              onClick={submit}
              disabled={saving || !!error?.stale}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {approving ? `Confirm refund of ${refundLabel}` : 'Confirm rejection'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReturnDecisionDialog;
